import { useEffect, useRef, useState } from 'react';
import type { TenantSelectionConflicts } from '../../../components/tenant-selector/types';
import { validateTenantSegments } from '../automations-tab/template-api';
import type { TenantSegmentConflict, TenantSelection } from '../automations-tab/templates.types';

const SEGMENT_NOTICE = {
  title: 'Tenant segment notice',
  description:
    "This automation uses segments that some of its tenants don't have yet, including tenants excluded from those segments. When you save and sync, those segments will be added to those tenants so the automation can run correctly.",
};

const selectionKey = (selection: TenantSelection): string =>
  [
    selection.selectionType,
    [...selection.selectedTenantIds].sort().join(','),
    [...selection.excludedTenantIds].sort().join(','),
  ].join('|');

export function useTenantSegmentConflicts(
  templateId: string,
  selection: TenantSelection,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  const [rawConflicts, setRawConflicts] = useState<TenantSegmentConflict[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const key = selectionKey(selection);

  useEffect(() => {
    const currentSelection = selectionRef.current;
    const isDraft =
      currentSelection.selectionType === 'selected' && currentSelection.selectedTenantIds.length === 0;
    if (!enabled || isDraft) {
      setRawConflicts([]);
      setIsValidating(false);
      return;
    }

    setIsValidating(true);
    let cancelled = false;
    const validate = async () => {
      try {
        const result = await validateTenantSegments(templateId, currentSelection);
        if (!cancelled) setRawConflicts(result.conflicts);
      } catch {
        if (!cancelled) setRawConflicts([]);
      } finally {
        if (!cancelled) setIsValidating(false);
      }
    };

    const debounce = setTimeout(validate, 500);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [templateId, key, enabled]);

  const items = rawConflicts.map(conflict => ({
    tenantId: conflict.tenantId,
    tenantName: conflict.tenantName,
    description: `will receive: ${conflict.missingSegments.map(segment => segment.name).join(', ')}`,
  }));
  const conflicts: TenantSelectionConflicts | null =
    items.length > 0 ? { notice: SEGMENT_NOTICE, items } : null;

  return { conflicts, isValidating };
}
